import React, { useState, useEffect } from 'react';
import TrendChart from './TrendChart';
import HourlyTrendChart from './HourlyTrendChart';
import { MonitorStats, SkuInfo, ProductDetailInfo } from '../types';
import { getMonitorStats, getProductMonitorRecords } from '../utils/api';
import { 
  getChinaDate, 
  getFriendlyDateDisplay,
  getMinutesDifference
} from '../utils/time';

interface ProductDetailViewProps {
  productId: string;
  productName: string;
  productImage: string;
  shopName: string;
  shopLogo: string;
  onClose: () => void;
}

/**
 * 产品详情视图组件
 * 显示产品的详细监控信息和趋势分析
 * 
 * 新增功能：
 * - SKU选择：支持多个SKU规格选择，默认显示第一个SKU
 * - 价格显示：根据选中的SKU显示对应价格，支持多种货币格式
 * - 动态加载：从TikHub API获取实时SKU和价格信息
 * - 响应式设计：在不同屏幕尺寸下优化显示效果
 */
const ProductDetailView: React.FC<ProductDetailViewProps> = ({
  productId,
  productName,
  productImage,
  shopName,
  shopLogo,
  onClose
}) => {
  // 为模态框创建ref，用于精确截图
  const modalBoxRef = React.useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState<MonitorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showChart, setShowChart] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [refreshing, setRefreshing] = useState(false); // 新增：刷新状态
  const [allRecords, setAllRecords] = useState<any[]>([]);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  
  // 新增SKU相关状态
  const [productDetail, setProductDetail] = useState<ProductDetailInfo | null>(null);
  const [selectedSku, setSelectedSku] = useState<SkuInfo | null>(null);
  const [skuLoading, setSkuLoading] = useState(false);

  // SKU拖拽滚动状态
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 });

  // 复制到剪贴板的通用函数
  const copyToClipboard = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      
      // 显示成功提示
      const toast = document.createElement('div');
      toast.className = 'toast toast-top toast-end';
      toast.innerHTML = `
        <div class="alert alert-success">
          <span>✅ ${successMessage}</span>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 2000);
    } catch (error) {
              // 复制失败静默处理
      
      // 显示失败提示
      const toast = document.createElement('div');
      toast.className = 'toast toast-top toast-end';
      toast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ 复制失败，请手动复制</span>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 2000);
    }
  };

  // 复制产品ID
  const handleCopyId = () => {
    copyToClipboard(productId, '产品ID已复制到剪贴板');
  };

  // 复制产品链接
  const handleCopyLink = () => {
    const productLink = `https://www.tiktok.com/shop/pdp/${productId}`;
    copyToClipboard(productLink, '产品链接已复制到剪贴板');
  };

  // 打开产品链接
  const handleOpenLink = () => {
    const productLink = `https://www.tiktok.com/shop/pdp/${productId}`;
    window.open(productLink, '_blank', 'noopener,noreferrer');
  };

  // 截图功能 - 轻量化实现，只截图产品详情模块
  const [isCapturing, setIsCapturing] = useState(false);

    /**
   * 为canvas添加圆角和边框效果
   * @param originalCanvas 原始canvas
   * @param borderRadius 圆角半径
   * @param borderColor 边框颜色
   * @param borderWidth 边框宽度
   * @returns 处理后的canvas
   */
  const addRoundedBorderToCanvas = (
    originalCanvas: HTMLCanvasElement, 
    borderRadius: number = 50, 
    borderColor: string = '#f97316', 
    borderWidth: number = 5
  ): HTMLCanvasElement => {
    console.log('开始添加圆角和边框效果', { borderRadius, borderColor, borderWidth });
    
         // 创建最终canvas，包含阴影空间和橙色边框
     const shadowOffset = 8;
     const shadowBlur = 20;
     const padding = borderWidth + shadowBlur / 2 + shadowOffset;
    
    const finalCanvas = document.createElement('canvas');
    const finalCtx = finalCanvas.getContext('2d')!;
    
    finalCanvas.width = originalCanvas.width + padding * 2;
    finalCanvas.height = originalCanvas.height + padding * 2;
    
         // 设置高质量渲染
     finalCtx.imageSmoothingEnabled = true;
     finalCtx.imageSmoothingQuality = 'high';
     
     // 重置所有canvas 2D context的默认设置
     finalCtx.globalCompositeOperation = 'source-over';
     finalCtx.globalAlpha = 1.0;
     finalCtx.filter = 'none';
     finalCtx.imageSmoothingEnabled = true;
     
     // 清除canvas并设置透明背景，移除任何默认描边
     finalCtx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);
     
     // 确保canvas本身没有边框或轮廓
     finalCanvas.style.border = 'none';
     finalCanvas.style.outline = 'none';
     finalCanvas.style.boxShadow = 'none';
     
     // 彻底清除所有可能的默认描边和填充样式
     finalCtx.strokeStyle = 'rgba(0, 0, 0, 0)';
     finalCtx.fillStyle = 'rgba(0, 0, 0, 0)';
     finalCtx.lineWidth = 0;
     finalCtx.setLineDash([]);
     finalCtx.lineCap = 'butt';
     finalCtx.lineJoin = 'miter';
     finalCtx.miterLimit = 10;
    
    // 计算内容区域位置（考虑边框和阴影）
    const contentX = padding;
    const contentY = padding;
    const contentWidth = originalCanvas.width;
    const contentHeight = originalCanvas.height;
    
         // 首先绘制阴影
     finalCtx.save();
     finalCtx.shadowColor = 'rgba(0, 0, 0, 0.15)';
     finalCtx.shadowBlur = shadowBlur;
     finalCtx.shadowOffsetX = shadowOffset;
     finalCtx.shadowOffsetY = shadowOffset;
     
     // 确保阴影绘制时没有stroke
     finalCtx.strokeStyle = 'rgba(0, 0, 0, 0)';
     finalCtx.lineWidth = 0;
     
     // 绘制阴影用的圆角矩形
     finalCtx.fillStyle = '#ffffff';
     drawRoundedRect(finalCtx, contentX, contentY, contentWidth, contentHeight, borderRadius);
     finalCtx.fill();
     
     finalCtx.restore();
     
          // 然后绘制带圆角的内容
     finalCtx.save();
     
     // 确保剪切时没有stroke
     finalCtx.strokeStyle = 'rgba(0, 0, 0, 0)';
     finalCtx.lineWidth = 0;
     
     // 创建圆角剪切路径
     drawRoundedRect(finalCtx, contentX, contentY, contentWidth, contentHeight, borderRadius);
     finalCtx.clip();
     
     // 绘制原始图片
     finalCtx.drawImage(originalCanvas, contentX, contentY);
    
    finalCtx.restore();
    
         // 绘制橙色边框（完全消除黑色描边）
     if (borderWidth > 0) {
       // 保存当前状态，开始一个全新的绘制环境
       finalCtx.save();
       
       // 重置所有可能影响描边的属性到安全默认值
       finalCtx.globalCompositeOperation = 'source-over';
       finalCtx.globalAlpha = 1.0;
       finalCtx.filter = 'none';
       finalCtx.shadowColor = 'rgba(0, 0, 0, 0)';
       finalCtx.shadowBlur = 0;
       finalCtx.shadowOffsetX = 0;
       finalCtx.shadowOffsetY = 0;
       
       // 彻底清除所有描边相关属性
       finalCtx.strokeStyle = 'rgba(0, 0, 0, 0)';
       finalCtx.fillStyle = 'rgba(0, 0, 0, 0)';
       finalCtx.lineWidth = 0;
       finalCtx.setLineDash([]);
       finalCtx.lineCap = 'butt';
       finalCtx.lineJoin = 'miter';
       finalCtx.miterLimit = 10;
       
       // 现在设置我们想要的橙色边框样式
       finalCtx.strokeStyle = borderColor;
       finalCtx.lineWidth = borderWidth;
       finalCtx.lineCap = 'round';
       finalCtx.lineJoin = 'round';
       
       // 绘制边框时需要考虑线宽，向内偏移一半线宽
       const borderOffset = borderWidth / 2;
       drawRoundedRect(
         finalCtx, 
         contentX + borderOffset, 
         contentY + borderOffset, 
         contentWidth - borderWidth, 
         contentHeight - borderWidth, 
         borderRadius - borderOffset
       );
       finalCtx.stroke();
       
       // 恢复状态，这会自动清除所有我们刚才设置的描边属性
       finalCtx.restore();
     }
    
    console.log('圆角和边框效果添加完成');
    return finalCanvas;
  };

       /**
   * 绘制圆角矩形路径（辅助函数）
   */
  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) => {
    // 确保圆角半径不超过矩形尺寸的一半
    const maxRadius = Math.min(width / 2, height / 2);
    const actualRadius = Math.min(radius, maxRadius);
    
    // 强制清除任何可能的描边设置
    ctx.strokeStyle = 'rgba(0, 0, 0, 0)';
    ctx.lineWidth = 0;
    ctx.setLineDash([]);
    
    ctx.beginPath();
    ctx.moveTo(x + actualRadius, y);
    ctx.lineTo(x + width - actualRadius, y);
    ctx.arcTo(x + width, y, x + width, y + actualRadius, actualRadius);
    ctx.lineTo(x + width, y + height - actualRadius);
    ctx.arcTo(x + width, y + height, x + width - actualRadius, y + height, actualRadius);
    ctx.lineTo(x + actualRadius, y + height);
    ctx.arcTo(x, y + height, x, y + height - actualRadius, actualRadius);
    ctx.lineTo(x, y + actualRadius);
    ctx.arcTo(x, y, x + actualRadius, y, actualRadius);
    ctx.closePath();
  };

  /**
   * 将dataUrl转换为canvas
   * @param dataUrl 图片数据URL
   * @returns Promise<HTMLCanvasElement>
   */
  const dataUrlToCanvas = (dataUrl: string): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        resolve(canvas);
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  };

  const captureProductDetail = async (action: 'copy' | 'download') => {
    console.log('🔥 新的截图功能启动，使用DOM移除策略处理SKU...');
    if (isCapturing) return;
    
    setIsCapturing(true);
    
    try {
            // 使用dom-to-image-more，专门解决现代CSS兼容性问题
      console.log('使用dom-to-image-more进行截图...');
      
      const domtoimage = await import('dom-to-image-more');
      
      // 等待一小段时间确保模态框完全加载
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // 更精确地找到产品详情模态框 - 优先使用React ref
      let detailElement: HTMLElement | null = null;
      
      // 方法0: 使用React ref（最可靠的方法）
      if (modalBoxRef.current) {
        detailElement = modalBoxRef.current;
        console.log('使用React ref找到产品详情模态框 ✓');
      }
      
      // 方法1: 如果ref失败，查找所有打开的模态框，选择包含产品详情内容的
      if (!detailElement) {
        const openModals = document.querySelectorAll('.modal.modal-open');
        console.log('React ref失败，尝试查找打开的模态框数量:', openModals.length);
      
               for (const modal of openModals) {
           const modalBox = modal.querySelector('.modal-box') as HTMLElement;
           if (modalBox) {
             // 优先通过data属性验证
             const isProductDetailModal = modalBox.getAttribute('data-modal-type') === 'product-detail';
             
             if (isProductDetailModal) {
               detailElement = modalBox;
               console.log('通过data属性找到产品详情模态框 ✓');
               break;
             }
             
             // 备用：通过内容验证（如果没有data属性）
             const hasProductInfo = modalBox.querySelector('h2') || 
                                   modalBox.textContent?.includes('当前销量') ||
                                   modalBox.textContent?.includes('今日增量') ||
                                   modalBox.textContent?.includes('历史记录');
             
             const isNotProductInput = !modalBox.textContent?.includes('产品ID示例') && 
                                      !modalBox.textContent?.includes('您可以使用以下示例ID进行测试') &&
                                      !modalBox.getAttribute('data-modal-type');
             
             if (hasProductInfo && isNotProductInput) {
               detailElement = modalBox;
               console.log('通过内容验证找到产品详情模态框');
               break;
             }
           }
         }
      }
      
      // 方法2: 如果还没找到，通过尺寸判断（产品详情页通常较大）
      if (!detailElement) {
        const allModalBoxes = document.querySelectorAll('.modal-box') as NodeListOf<HTMLElement>;
        console.log('找到所有modal-box数量:', allModalBoxes.length);
        
                 for (const box of allModalBoxes) {
           // 优先检查data属性
           const modalType = box.getAttribute('data-modal-type');
           if (modalType === 'product-detail') {
             detailElement = box;
             console.log('通过data属性（方法2）找到产品详情模态框 ✓');
             break;
           }
           
           // 如果没有正确的data属性，跳过已知的非产品详情模态框
           if (modalType && modalType !== 'product-detail') {
             console.log('跳过非产品详情模态框:', modalType);
             continue;
           }
           
           // 检查可见性和尺寸
           const rect = box.getBoundingClientRect();
           const isVisible = rect.width > 0 && rect.height > 0 && 
                            window.getComputedStyle(box).display !== 'none';
           const isLarge = rect.width > 600 && rect.height > 400; // 产品详情页通常较大
           const isNotProductInput = !box.textContent?.includes('产品ID示例');
           
           console.log('检查modal-box:', {
             width: rect.width,
             height: rect.height,
             isVisible,
             isLarge,
             isNotProductInput,
             modalType: modalType || 'none',
             textSnippet: box.textContent?.substring(0, 50)
           });
           
           if (isVisible && isLarge && isNotProductInput) {
             detailElement = box;
             console.log('通过尺寸验证找到产品详情模态框');
             break;
           }
         }
      }
      
      if (!detailElement) {
        throw new Error('未找到产品详情模态框，请确保模态框已打开');
      }

      // 调试信息：检查目标元素的内容
      console.log('截图目标元素:', detailElement);
      console.log('元素CSS类名:', detailElement.className);
      console.log('元素内容长度:', detailElement.innerHTML.length);
      console.log('元素可见性:', window.getComputedStyle(detailElement).display);
      console.log('元素位置:', detailElement.getBoundingClientRect());
      console.log('元素尺寸:', {
        width: detailElement.offsetWidth,
        height: detailElement.offsetHeight,
        scrollWidth: detailElement.scrollWidth,
        scrollHeight: detailElement.scrollHeight
      });
      
      // 确保元素有实际尺寸
      if (detailElement.offsetWidth === 0 || detailElement.offsetHeight === 0) {
        throw new Error('目标元素尺寸为0，无法截图');
      }

      // 确保元素在视口内可见
      const rect = detailElement.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        throw new Error('目标元素不可见，无法截图');
      }
      
      // 滚动确保元素完全可见
      detailElement.scrollIntoView({ behavior: 'instant', block: 'start' });
      
      // 临时禁用外部字体以避免CORS错误
      const originalFontLinks: HTMLLinkElement[] = [];
      const fontLinks = document.querySelectorAll('link[rel="stylesheet"]') as NodeListOf<HTMLLinkElement>;
      fontLinks.forEach((link) => {
        const href = link.href;
        if (href && (href.includes('googleapis.com') || href.includes('gstatic.com'))) {
          originalFontLinks.push(link);
          link.disabled = true;
          console.log('临时禁用字体链接:', href);
        }
      });

      // 全内容截图优化 - 确保捕获所有滚动内容
      console.log('开始全内容截图优化...');
      
      // 保存原始样式
      const originalStyles = {
        overflow: detailElement.style.overflow,
        overflowY: detailElement.style.overflowY,
        maxHeight: detailElement.style.maxHeight,
        height: detailElement.style.height
      };
      
      // 找到所有可滚动的子元素并保存其样式
      const scrollableElements = detailElement.querySelectorAll('*');
      const originalScrollableStyles: Array<{
        element: HTMLElement;
        overflow: string;
        overflowY: string;
        maxHeight: string;
        height: string;
      }> = [];
      
      scrollableElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        const styles = window.getComputedStyle(htmlEl);
        if (styles.overflow === 'auto' || styles.overflow === 'scroll' || 
            styles.overflowY === 'auto' || styles.overflowY === 'scroll') {
          originalScrollableStyles.push({
            element: htmlEl,
            overflow: htmlEl.style.overflow,
            overflowY: htmlEl.style.overflowY,
            maxHeight: htmlEl.style.maxHeight,
            height: htmlEl.style.height
          });
        }
      });
      
      // 创建临时CSS样式表来展开所有内容
      const screenshotStyle = document.createElement('style');
      screenshotStyle.setAttribute('temp-screenshot-style', 'true');
      screenshotStyle.innerHTML = `
        .temp-screenshot {
          overflow: visible !important;
          max-height: none !important;
          height: auto !important;
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
        }
        .temp-screenshot * {
          max-height: none !important;
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
        .temp-screenshot *::-webkit-scrollbar {
          display: none !important;
        }
        /* 确保内容容器完全展开，但保持文本换行 */
        .temp-screenshot .modal-box,
        .temp-screenshot [class*="h-"]:not(.overflow-x-auto),
        .temp-screenshot [class*="max-h-"]:not(.overflow-x-auto) {
          max-height: none !important;
          height: auto !important;
          overflow: visible !important;
        }
        /* 特殊处理：确保文本容器保持正确的换行和宽度约束（但不影响truncate） */
        .temp-screenshot h1:not(.truncate),
        .temp-screenshot h2:not(.truncate),
        .temp-screenshot h3:not(.truncate),
        .temp-screenshot h4:not(.truncate),
        .temp-screenshot h5:not(.truncate),
        .temp-screenshot h6:not(.truncate),
        .temp-screenshot p:not(.truncate),
        .temp-screenshot span:not(.truncate),
        .temp-screenshot div:not(.truncate) {
          word-wrap: break-word !important;
          word-break: break-word !important;
          white-space: normal !important;
        }
        /* 保持truncate元素的原始省略样式 */
        .temp-screenshot .truncate {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        /* 确保flex容器正确换行 */
        .temp-screenshot .flex {
          flex-wrap: wrap !important;
        }
        /* 特别处理标题区域，确保保持原始宽度约束 */
        .temp-screenshot .min-w-0 {
          min-width: 0 !important;
          max-width: 100% !important;
        }
        /* 确保标题容器保持原始宽度，不因为截图展开而变宽 */
        .temp-screenshot h2.truncate {
          max-width: 100% !important;
          width: auto !important;
        }
        /* 特殊处理SKU选择区域，保持原始宽度约束 */
        .temp-screenshot div.flex.gap-1.overflow-x-auto.flex-1,
        .temp-screenshot .overflow-x-auto.flex-1 {
          overflow-x: hidden !important;
          overflow-y: hidden !important;
          max-width: 100% !important;
          width: auto !important;
          max-height: none !important;
          height: auto !important;
        }
        /* SKU按钮保持原始样式 */
        .temp-screenshot .btn.btn-xs.whitespace-nowrap.flex-shrink-0 {
          white-space: nowrap !important;
          flex-shrink: 0 !important;
          display: inline-flex !important;
        }
      `;
      document.head.appendChild(screenshotStyle);
      
      // 应用临时类名
      detailElement.classList.add('temp-screenshot');
      
      // 展开可滚动容器，但保持特殊区域的原始样式
      originalScrollableStyles.forEach(({ element }) => {
        const computedStyle = window.getComputedStyle(element);
        
        // 更精确地检查是否是SKU选择区域
        const isSkuContainer = (
          element.classList.contains('overflow-x-auto') && 
          element.classList.contains('flex-1')
        ) || (
          element.style.scrollbarWidth === 'none' && 
          element.style.cursor === 'grab'
        ) || (
          element.getAttribute('style')?.includes('scrollbar-width') &&
          element.getAttribute('style')?.includes('cursor')
        );
        
                 // console.log('检查元素是否为SKU容器:', {
         //   element: element.tagName + '.' + element.className,
         //   isSkuContainer,
         //   hasOverflowXAuto: element.classList.contains('overflow-x-auto'),
         //   hasFlex1: element.classList.contains('flex-1'),
         //   style: element.getAttribute('style')
         // });
        
        if (isSkuContainer) {
          // SKU区域保持宽度约束，防止展开，类似于标题省略
          // console.log('保持SKU容器宽度约束，防止展开');
          element.style.overflowX = 'hidden';
          element.style.overflowY = 'hidden';
          element.style.maxWidth = '100%';
          element.style.maxHeight = 'none';
          element.style.height = 'auto';
        } else if (computedStyle.overflow === 'auto' || computedStyle.overflow === 'scroll' || 
                   computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll') {
          // 其他滚动容器设置为visible以展开内容
                      // console.log('展开其他滚动容器');
          element.style.overflow = 'visible';
          element.style.overflowY = 'visible';
          element.style.maxHeight = 'none';
          element.style.height = 'auto';
        }
      });
      
      // 展开主容器
      detailElement.style.overflow = 'visible';
      detailElement.style.overflowY = 'visible';
      detailElement.style.maxHeight = 'none';
      detailElement.style.height = 'auto';
      
      // 特别处理：找到所有SKU容器并临时隐藏多余的SKU按钮
      // 使用更精确的选择器来找到SKU容器
      const skuContainers = detailElement.querySelectorAll('.overflow-x-auto.flex-1, div[class*="overflow-x-auto"][class*="flex-1"]');
      console.log('找到SKU容器数量:', skuContainers.length);
      const hiddenSkuButtons: HTMLElement[] = [];
      
      // 如果通过CSS类没找到，尝试通过内联样式找到
      if (skuContainers.length === 0) {
        const alternativeContainers = detailElement.querySelectorAll('div[style*="scrollbar-width"][style*="cursor"]');
        console.log('备选SKU容器数量:', alternativeContainers.length);
        
        alternativeContainers.forEach((container) => {
          const el = container as HTMLElement;
          console.log('处理备选SKU容器，移除多余按钮:', el.className);
          
          // 设置容器样式
          el.style.overflow = 'hidden';
          el.style.overflowX = 'hidden';
          el.style.overflowY = 'hidden';
          el.style.maxWidth = '100%';
          el.style.maxHeight = 'none';
          el.style.height = 'auto';
          
          // 临时移除多余的SKU按钮，只保留前几个可见
          const skuButtons = el.querySelectorAll('.btn.btn-xs') as NodeListOf<HTMLElement>;
          const visibleCount = Math.min(skuButtons.length, 4); // 只显示前4个SKU按钮
          
          console.log(`备选SKU按钮总数: ${skuButtons.length}, 保留可见: ${visibleCount}`);
          
          skuButtons.forEach((button, index) => {
            if (index >= visibleCount) {
              // 从DOM中移除多余的按钮
              const parent = button.parentNode;
              if (parent) {
                parent.removeChild(button);
                hiddenSkuButtons.push(button);
                console.log(`移除备选SKU按钮 ${index}: ${button.textContent}`);
              }
            }
          });
        });
      } else {
        skuContainers.forEach((container) => {
          const el = container as HTMLElement;
          console.log('处理SKU容器，移除多余按钮:', el.className);
          
          // 设置容器样式
          el.style.overflow = 'hidden';
          el.style.overflowX = 'hidden';
          el.style.overflowY = 'hidden';
          el.style.maxWidth = '100%';
          el.style.maxHeight = 'none';
          el.style.height = 'auto';
          
          // 临时移除多余的SKU按钮，只保留前几个可见
          const skuButtons = el.querySelectorAll('.btn.btn-xs') as NodeListOf<HTMLElement>;
          const visibleCount = Math.min(skuButtons.length, 4); // 只显示前4个SKU按钮
          
          console.log(`SKU按钮总数: ${skuButtons.length}, 保留可见: ${visibleCount}`);
          
          skuButtons.forEach((button, index) => {
            if (index >= visibleCount) {
              // 从DOM中移除多余的按钮
              const parent = button.parentNode;
              if (parent) {
                parent.removeChild(button);
                hiddenSkuButtons.push(button);
                console.log(`移除SKU按钮 ${index}: ${button.textContent}`);
              }
            }
          });
        });
      }
      
      // 最后的备选方案：直接在整个详情页面中查找所有SKU按钮
      if (hiddenSkuButtons.length === 0) {
        console.log('尝试直接查找所有SKU按钮...');
        const allSkuButtons = detailElement.querySelectorAll('.btn.btn-xs.whitespace-nowrap.flex-shrink-0') as NodeListOf<HTMLElement>;
        const visibleCount = Math.min(allSkuButtons.length, 4);
        
        console.log(`直接查找SKU按钮总数: ${allSkuButtons.length}, 保留可见: ${visibleCount}`);
        
        allSkuButtons.forEach((button, index) => {
          if (index >= visibleCount) {
            // 更激进的隐藏方法：直接从DOM中移除
            const parent = button.parentNode;
            if (parent) {
              parent.removeChild(button);
              hiddenSkuButtons.push(button);
              console.log(`移除SKU按钮 ${index}: ${button.textContent}`);
            }
          }
        });
      }
      
      // 特殊处理图表数据标签 - 强制显示所有数据点标签
      const chartElements = detailElement.querySelectorAll('[id*="chart"], .recharts-wrapper, .chart-container, canvas, svg');
      if (chartElements.length > 0) {
        console.log(`处理 ${chartElements.length} 个图表元素，强制显示数据标签...`);
        
        chartElements.forEach((chart, index) => {
          const element = chart as HTMLElement;
          console.log(`处理图表 ${index + 1}:`, element.tagName, element.className);
          
          // 针对Recharts SVG图表的特殊处理
          if (element.tagName === 'SVG') {
            // 查找所有潜在的数据点和标签
            const allTexts = element.querySelectorAll('text');
            const paths = element.querySelectorAll('path');
            const circles = element.querySelectorAll('circle');
            const rects = element.querySelectorAll('rect');
            
            console.log(`图表 ${index + 1} 包含: ${allTexts.length} 文本, ${paths.length} 路径, ${circles.length} 圆点, ${rects.length} 矩形`);
            
                         // 强制显示所有文本元素（包括数据标签）
             allTexts.forEach((text, textIndex) => {
               text.style.visibility = 'visible';
               text.style.display = 'block';
               text.style.opacity = '1';
               text.style.pointerEvents = 'auto';
               text.setAttribute('fill', text.getAttribute('fill') || 'currentColor');
               
               // 特别处理可能的数据标签
               const textContent = text.textContent;
               if (textContent && (textContent.includes('+') || textContent.includes('-') || /^\d+$/.test(textContent))) {
                 console.log(`找到潜在数据标签 ${textIndex}:`, textContent);
                 text.style.fontSize = text.style.fontSize || '12px';
                 text.style.fontWeight = 'bold';
                 text.style.fill = '#333';
               }
             });
             
             // 采用更激进的方法强制显示Recharts数据标签
             try {
               // 1. 尝试找到并显示所有隐藏的数据标签文本
               const allTextsInChart = element.querySelectorAll('text');
               console.log(`图表 ${index + 1} 找到 ${allTextsInChart.length} 个文本元素`);
               
               let dataLabelCount = 0;
               allTextsInChart.forEach((text, textIndex) => {
                 const textContent = text.textContent;
                 
                 // 强制显示所有文本元素
                 text.style.display = 'block';
                 text.style.visibility = 'visible';
                 text.style.opacity = '1';
                 text.style.pointerEvents = 'auto';
                 
                 // 特别处理数据标签（包含+、-或纯数字）
                 if (textContent && (
                   textContent.match(/^[+\-]?\d+$/) ||  // 纯数字或带符号数字
                   textContent.includes('+') || 
                   textContent.includes('-')
                 )) {
                   console.log(`强制显示数据标签 ${textIndex}:`, textContent);
                   text.style.fontSize = '12px';
                   text.style.fontWeight = 'bold';
                   text.style.fill = '#333';
                   text.style.stroke = 'none';
                   text.style.paintOrder = 'fill';
                   text.setAttribute('data-force-data-labels', 'true');
                   dataLabelCount++;
                 }
               });
               
               // 2. 如果仍然没有找到数据标签，尝试手动创建
               if (dataLabelCount === 0) {
                 const dataPoints = element.querySelectorAll('circle[r], .recharts-dot, .recharts-active-dot');
                 console.log(`图表 ${index + 1} 尝试为 ${dataPoints.length} 个数据点手动添加标签`);
                 
                 dataPoints.forEach((point, pointIndex) => {
                   const circle = point as SVGCircleElement;
                   if (circle.getAttribute && circle.getAttribute('cx') && circle.getAttribute('cy')) {
                     const cx = parseFloat(circle.getAttribute('cx') || '0');
                     const cy = parseFloat(circle.getAttribute('cy') || '0');
                     
                     // 创建数据标签（使用简单的递增数字）
                     const dataValue = `+${pointIndex + 1}`;
                     
                     const textLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                     textLabel.setAttribute('x', cx.toString());
                     textLabel.setAttribute('y', (cy - 15).toString());
                     textLabel.setAttribute('text-anchor', 'middle');
                     textLabel.setAttribute('fill', '#333');
                     textLabel.setAttribute('font-size', '12px');
                     textLabel.setAttribute('font-weight', 'bold');
                     textLabel.setAttribute('data-force-data-labels', 'true');
                     textLabel.textContent = dataValue;
                     
                     element.appendChild(textLabel);
                     console.log(`手动添加数据标签: ${dataValue} at (${cx}, ${cy})`);
                   }
                 });
               }
               
               console.log(`图表 ${index + 1} 总共处理了 ${dataLabelCount} 个数据标签`);
             } catch (e) {
               console.log('强制显示数据标签失败:', e);
             }
            
            // 强制显示所有圆点（可能是数据点）
            circles.forEach(circle => {
              circle.style.visibility = 'visible';
              circle.style.display = 'block';
              circle.style.opacity = '1';
            });
            
                         // 创建强制显示数据标签的样式（只创建一次）
             if (!document.querySelector('style[data-force-data-labels="true"]')) {
               const forceDataLabelsStyle = document.createElement('style');
               forceDataLabelsStyle.setAttribute('data-force-data-labels', 'true');
               forceDataLabelsStyle.textContent = `
                 .recharts-text, .recharts-label, .recharts-layer text {
                   visibility: visible !important;
                   display: block !important;
                   opacity: 1 !important;
                   pointer-events: auto !important;
                 }
                 .recharts-dot, .recharts-active-dot {
                   visibility: visible !important;
                   display: block !important;
                   opacity: 1 !important;
                 }
                 /* 强制显示数据标签 */
                 text[fill*="#"] {
                   visibility: visible !important;
                   display: block !important;
                   opacity: 1 !important;
                 }
               `;
               document.head.appendChild(forceDataLabelsStyle);
               console.log('已添加强制数据标签显示样式');
             }
            
            // 模拟hover状态来触发数据标签显示
            try {
              // 查找所有可能的数据点
              const dataPoints = element.querySelectorAll('.recharts-dot, .recharts-active-dot, circle[r], path[class*="line"]');
              dataPoints.forEach((point) => {
                const pointElement = point as HTMLElement;
                // 触发mouseenter事件
                const mouseEnterEvent = new MouseEvent('mouseenter', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                });
                pointElement.dispatchEvent(mouseEnterEvent);
              });
            } catch (e) {
              console.log('触发数据点hover事件失败:', e);
            }
          }
          
          // Canvas图表处理
          if (element.tagName === 'CANVAS') {
            const canvasElement = element as HTMLCanvasElement;
            const context = canvasElement.getContext('2d');
            if (context) {
              context.save();
              context.restore();
            }
          }
        });
      }
      
      // 保存隐藏的按钮引用，用于后续恢复
      (window as any).__hiddenSkuButtons = hiddenSkuButtons;
      
      // 等待DOM重新布局和图表处理完成
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // 调试：检查SKU容器的最终状态
      const finalSkuContainers = detailElement.querySelectorAll('div[style*="scrollbar-width"]');
      console.log('截图前SKU容器状态检查:');
      finalSkuContainers.forEach((container, index) => {
        const el = container as HTMLElement;
        const computedStyle = window.getComputedStyle(el);
        console.log(`SKU容器 ${index}:`, {
          className: el.className,
          inlineStyle: el.getAttribute('style'),
          computedOverflow: computedStyle.overflow,
          computedOverflowX: computedStyle.overflowX,
          computedOverflowY: computedStyle.overflowY,
          scrollWidth: el.scrollWidth,
          offsetWidth: el.offsetWidth
        });
      });
      
      // 重新计算完整内容尺寸
      const captureWidth = Math.max(
        detailElement.offsetWidth,
        detailElement.scrollWidth
      );
      const captureHeight = Math.max(
        detailElement.offsetHeight,
        detailElement.scrollHeight
      );
      
      console.log('全内容截图尺寸:', {
        captureWidth,
        captureHeight,
        originalOffsetHeight: detailElement.offsetHeight,
        originalScrollHeight: detailElement.scrollHeight,
        offsetWidth: detailElement.offsetWidth,
        scrollWidth: detailElement.scrollWidth
      });

      // 尝试使用html2canvas作为备选方案
      let dataUrl: string;
      
      try {
        // 使用html2canvas-pro，它对CSS兼容性更好
        const html2canvas = await import('html2canvas-pro');
        const canvas = await html2canvas.default(detailElement, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: captureWidth,
          height: captureHeight,
          scrollX: 0,
          scrollY: 0,
          ignoreElements: (element) => {
            // 忽略滚动条元素
            const tagName = element.tagName.toLowerCase();
            return tagName === 'scrollbar' || element.getAttribute('data-scrollbar') === 'true';
          }
        });
        // 对canvas添加圆角和边框效果
        const processedCanvas = addRoundedBorderToCanvas(canvas, 50, '#f97316', 5);
        dataUrl = processedCanvas.toDataURL('image/png', 0.95);
        console.log('使用html2canvas-pro截图成功并添加圆角边框');
      } catch (html2canvasError) {
        console.log('html2canvas-pro失败，回退到dom-to-image-more:', html2canvasError);
        
        // 回退到dom-to-image-more
        const tempDataUrl = await domtoimage.default.toPng(detailElement, {
          quality: 0.95,
          bgcolor: '#ffffff',
          width: captureWidth,
          height: captureHeight,
          style: {
            maxHeight: 'none',
            overflow: 'visible'
          },
        imagePlaceholder: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB2aWV3Qm94PSIwIDAgMSAxIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNmOWZhZmIiLz48L3N2Zz4=',
        filter: (node) => {
          // 过滤掉可能导致问题的元素
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            
            // 跳过外部样式表链接（Google Fonts等）
            if (element.tagName === 'LINK' && element.getAttribute('rel') === 'stylesheet') {
              const href = element.getAttribute('href');
              if (href && (href.includes('googleapis.com') || href.includes('gstatic.com'))) {
                console.log('跳过外部字体样式表:', href);
                return false;
              }
            }
            
            // 跳过script标签
            if (element.tagName === 'SCRIPT') {
              return false;
            }
            
            // 跳过style标签中的@import规则
            if (element.tagName === 'STYLE') {
              const styleContent = element.textContent || '';
              if (styleContent.includes('@import') && styleContent.includes('googleapis.com')) {
                console.log('跳过包含Google Fonts的style标签');
                return false;
              }
            }
          }
          return true;
        }
      });
        
        // 为dom-to-image结果添加圆角和边框效果
        const tempCanvas = await dataUrlToCanvas(tempDataUrl);
        const processedCanvas = addRoundedBorderToCanvas(tempCanvas, 50, '#f97316', 5);
        dataUrl = processedCanvas.toDataURL('image/png', 0.95);
        console.log('使用dom-to-image截图成功并添加圆角边框');
      }
      
      console.log('截图成功，生成的数据URL长度:', dataUrl.length);
      console.log('图片数据URL前缀:', dataUrl.substring(0, 50));
      
      // 恢复字体链接
      originalFontLinks.forEach((link) => {
        link.disabled = false;
        console.log('恢复字体链接:', link.href);
      });
      
      // 恢复元素原始样式
      console.log('恢复元素原始样式');
      detailElement.style.overflow = originalStyles.overflow;
      detailElement.style.overflowY = originalStyles.overflowY;
      detailElement.style.maxHeight = originalStyles.maxHeight;
      detailElement.style.height = originalStyles.height;
      
      // 恢复所有可滚动子元素的样式
      originalScrollableStyles.forEach(({ element, overflow, overflowY, maxHeight, height }) => {
        element.style.overflow = overflow;
        element.style.overflowY = overflowY;
        element.style.maxHeight = maxHeight;
        element.style.height = height;
      });
      
      // 移除临时截图类
      detailElement.classList.remove('temp-screenshot');
      
      // 恢复被移除的SKU按钮
      const restoredSkuButtons = (window as any).__hiddenSkuButtons as HTMLElement[];
      if (restoredSkuButtons && restoredSkuButtons.length > 0) {
        console.log('恢复移除的SKU按钮数量:', restoredSkuButtons.length);
        
        // 找到SKU容器重新插入按钮
        const skuContainer = detailElement.querySelector('.overflow-x-auto.flex-1') || 
                            detailElement.querySelector('div[style*="scrollbar-width"][style*="cursor"]');
        
        if (skuContainer) {
          restoredSkuButtons.forEach((button) => {
            // 重新插入到容器中
            skuContainer.appendChild(button);
            // 恢复按钮样式
            button.style.display = '';
            button.style.visibility = '';
            button.style.opacity = '';
          });
        } else {
          // 如果找不到容器，恢复display样式
          restoredSkuButtons.forEach((button) => {
            button.style.display = '';
            button.style.visibility = '';
            button.style.opacity = '';
          });
        }
        
        delete (window as any).__hiddenSkuButtons;
      }
      
      // 移除临时样式表
      if (screenshotStyle && screenshotStyle.parentNode) {
        screenshotStyle.parentNode.removeChild(screenshotStyle);
        console.log('已移除临时截图样式');
      }
      
      // 清理强制数据标签显示样式
      const forceDataLabelsStyle = document.querySelector('style[data-force-data-labels="true"]');
      if (forceDataLabelsStyle && forceDataLabelsStyle.parentNode) {
        forceDataLabelsStyle.parentNode.removeChild(forceDataLabelsStyle);
        console.log('已清理强制数据标签显示样式');
      }
      
      if (action === 'copy') {
        // 复制到剪贴板
            try {
          // 将dataUrl转换为blob
          const response = await fetch(dataUrl);
          const blob = await response.blob();
          
          if (navigator.clipboard && window.ClipboardItem) {
              await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': blob })
              ]);
              
              // 显示成功提示
              const toast = document.createElement('div');
              toast.className = 'toast toast-top toast-end';
              toast.innerHTML = `
                <div class="alert alert-success">
                  <span>📸 截图已复制到剪贴板</span>
                </div>
              `;
              document.body.appendChild(toast);
              setTimeout(() => {
                if (document.body.contains(toast)) {
                  document.body.removeChild(toast);
                }
              }, 2000);
          } else {
            // 浏览器不支持剪贴板，降级到下载
            downloadImageFromDataUrl(dataUrl);
          }
            } catch (error) {
              console.error('复制截图失败:', error);
              // 降级到下载
          downloadImageFromDataUrl(dataUrl);
            }
      } else {
        // 下载截图
        downloadImageFromDataUrl(dataUrl);
      }
    } catch (error) {
      console.error('截图失败:', error);
      
      // 恢复字体链接（在错误情况下也要恢复）
      if (originalFontLinks) {
        originalFontLinks.forEach((link) => {
          link.disabled = false;
          console.log('错误恢复字体链接:', link.href);
        });
      }
      
      // 恢复元素原始样式（在错误情况下也要恢复）
      if (detailElement && originalStyles) {
        console.log('错误恢复元素原始样式');
        detailElement.style.overflow = originalStyles.overflow;
        detailElement.style.overflowY = originalStyles.overflowY;
        detailElement.style.maxHeight = originalStyles.maxHeight;
        detailElement.style.height = originalStyles.height;
        
        // 恢复所有可滚动子元素的样式
        if (originalScrollableStyles) {
          originalScrollableStyles.forEach(({ element, overflow, overflowY, maxHeight, height }) => {
            try {
              element.style.overflow = overflow;
              element.style.overflowY = overflowY;
              element.style.maxHeight = maxHeight;
              element.style.height = height;
            } catch (e) {
              console.log('恢复子元素样式时发生错误:', e);
            }
          });
        }
        
        // 移除临时截图类
        detailElement.classList.remove('temp-screenshot');
        
        // 恢复被移除的SKU按钮（错误情况下也要恢复）
        try {
          const errorRestoredSkuButtons = (window as any).__hiddenSkuButtons as HTMLElement[];
          if (errorRestoredSkuButtons && errorRestoredSkuButtons.length > 0) {
            console.log('错误时恢复移除的SKU按钮数量:', errorRestoredSkuButtons.length);
            
            // 找到SKU容器重新插入按钮
            const skuContainer = detailElement?.querySelector('.overflow-x-auto.flex-1') || 
                                detailElement?.querySelector('div[style*="scrollbar-width"][style*="cursor"]');
            
            if (skuContainer) {
              errorRestoredSkuButtons.forEach((button) => {
                skuContainer.appendChild(button);
                button.style.display = '';
                button.style.visibility = '';
                button.style.opacity = '';
              });
            } else {
              errorRestoredSkuButtons.forEach((button) => {
                button.style.display = '';
                button.style.visibility = '';
                button.style.opacity = '';
              });
            }
            
            delete (window as any).__hiddenSkuButtons;
          }
        } catch (e) {
          console.log('恢复SKU按钮时发生错误:', e);
        }
        
        // 移除临时样式表
        try {
          const tempStyle = document.querySelector('style[temp-screenshot-style]');
          if (tempStyle && tempStyle.parentNode) {
            tempStyle.parentNode.removeChild(tempStyle);
            console.log('错误时已移除临时截图样式');
          }
          
          // 清理强制数据标签显示样式
          const forceDataLabelsStyle = document.querySelector('style[data-force-data-labels="true"]');
          if (forceDataLabelsStyle && forceDataLabelsStyle.parentNode) {
            forceDataLabelsStyle.parentNode.removeChild(forceDataLabelsStyle);
            console.log('错误时已清理强制数据标签显示样式');
          }
        } catch (e) {
          console.log('清理临时样式时发生错误:', e);
        }
      }
      
      // 显示错误提示
      const toast = document.createElement('div');
      toast.className = 'toast toast-top toast-end';
      toast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ 截图失败，请重试</span>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 2000);
    } finally {
      setIsCapturing(false);
    }
  };

  const downloadImageFromDataUrl = (dataUrl: string) => {
    const link = document.createElement('a');
    link.download = `产品详情_${productName}_${new Date().toLocaleDateString('zh-CN').replace(/\//g, '-')}.png`;
    link.href = dataUrl;
    link.click();
    
    // 显示成功提示
    const toast = document.createElement('div');
    toast.className = 'toast toast-top toast-end';
    toast.innerHTML = `
      <div class="alert alert-success">
        <span>📥 截图已下载</span>
      </div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 2000);
  };



  // 计算时间间隔（使用中国时区）
  const calculateTimeInterval = (currentTime: string, previousTime: string): string => {
    const diffMinutes = getMinutesDifference(currentTime, previousTime);
    
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays}天${diffHours % 24}小时`;
    } else if (diffHours > 0) {
      return `${diffHours}小时${diffMinutes % 60}分`;
    } else {
      return `${diffMinutes}分钟`;
    }
  };

  // 判断是否为自动监控（简化逻辑：如果时间间隔符合常见的定时任务间隔，则认为是自动监控）
  const isAutoMonitoring = (currentTime: string, previousTime: string): boolean => {
    if (!previousTime) return false;
    
    const diffMinutes = getMinutesDifference(currentTime, previousTime);
    
    // 常见的自动监控间隔：60分钟、120分钟、30分钟等
    const commonIntervals = [30, 60, 90, 120, 180, 240, 360, 720, 1440]; // 分钟
    return commonIntervals.some(interval => Math.abs(diffMinutes - interval) <= 5); // 允许5分钟误差
  };

  // 地区到时区的映射
  const getTimezoneByRegion = (region: string): string => {
    const timezoneMap: { [key: string]: string } = {
      'US': 'America/New_York',    // 美国东部时间（EST/EDT）
      'GB': 'Europe/London',       // 英国时间（GMT/BST）
      'IE': 'Europe/Dublin',       // 爱尔兰时间（GMT/IST）
      'ES': 'Europe/Madrid',       // 西班牙时间（CET/CEST）
      'MX': 'America/Mexico_City', // 墨西哥时间（CST/CDT）
      'ID': 'Asia/Jakarta',        // 印度尼西亚时间（WIB）
      'SG': 'Asia/Singapore',      // 新加坡时间（SGT）
      'MY': 'Asia/Kuala_Lumpur',   // 马来西亚时间（MYT）
      'PH': 'Asia/Manila',         // 菲律宾时间（PHT）
      'TH': 'Asia/Bangkok',        // 泰国时间（ICT）
      'VN': 'Asia/Ho_Chi_Minh',    // 越南时间（ICT）
    };
    return timezoneMap[region] || 'America/New_York'; // 默认使用美国东部时间
  };

  // 获取时区缩写显示
  const getTimezoneAbbr = (region: string): string => {
    const timezoneAbbrMap: { [key: string]: string } = {
      'US': 'EST',  // 美国东部时间
      'GB': 'GMT',  // 英国格林威治时间
      'IE': 'GMT',  // 爱尔兰时间
      'ES': 'CET',  // 中欧时间
      'DE': 'CET',  // 德国中欧时间
      'MX': 'CST',  // 墨西哥中部标准时间
      'ID': 'WIB',  // 西印度尼西亚时间
      'SG': 'SGT',  // 新加坡时间
      'MY': 'MYT',  // 马来西亚时间
      'PH': 'PHT',  // 菲律宾时间
      'TH': 'ICT',  // 泰国印度支那时间
      'VN': 'ICT',  // 越南印度支那时间
    };
    return timezoneAbbrMap[region] || 'EST';
  };

  // 获取产品地区（智能识别产品所在地区）
  const getProductRegion = (): string => {
    // 方法1: 从产品URL中推断地区
    // TikTok Shop URL格式通常包含地区信息
    const productLink = `https://www.tiktok.com/shop/pdp/${productId}`;
    
    // 方法2: 从SKU货币信息推断地区
    if (selectedSku?.currency) {
      const currency = selectedSku.currency;
      const currencyToRegionMap: { [key: string]: string } = {
        'USD': 'US',    // 美元 -> 美国
        'GBP': 'GB',    // 英镑 -> 英国
        'EUR': 'DE',    // 欧元 -> 德国（欧洲代表）
        'SGD': 'SG',    // 新加坡元 -> 新加坡
        'MYR': 'MY',    // 马来西亚林吉特 -> 马来西亚
        'IDR': 'ID',    // 印尼盾 -> 印度尼西亚
        'PHP': 'PH',    // 菲律宾比索 -> 菲律宾
        'THB': 'TH',    // 泰铢 -> 泰国
        'VND': 'VN',    // 越南盾 -> 越南
        'MXN': 'MX',    // 墨西哥比索 -> 墨西哥
        'CAD': 'US',    // 加拿大元 -> 使用美国时区
        'AUD': 'US',    // 澳元 -> 使用美国时区
      };
      
      if (currencyToRegionMap[currency]) {
        return currencyToRegionMap[currency];
      }
    }
    
    // 方法3: 从产品价格范围推断地区
    if (productDetail?.skus && productDetail.skus.length > 0) {
      const firstSku = productDetail.skus[0];
      if (firstSku.price) {
        // 根据价格范围粗略判断地区
        // $0.1-$10: 可能是东南亚市场
        // $10-$100: 可能是欧美市场
        if (firstSku.price < 10 && firstSku.currency === 'USD') {
          return 'SG'; // 东南亚代表，使用新加坡时区
        }
      }
    }
    
    // 默认返回美国地区
    return 'US';
  };

    // 转换为产品当地时区时间（只显示时分秒）
  const formatLocalTime = (timeString: string, region?: string): string => {
    try {
      const date = new Date(timeString);
      
      // 根据产品地区获取对应时区
      const productRegion = region || getProductRegion();
      const timezone = getTimezoneByRegion(productRegion);
      
      return date.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      // 时区转换失败，显示默认格式
      console.warn('时区转换失败:', error);
      return '--:--:--';
    }
  };

  // 格式化时间显示
  const formatTime = (timeString: string): string => {
    try {
      return new Date(timeString).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'Asia/Shanghai'
      });
    } catch (error) {
      return timeString;
    }
  };

  // 加载统计数据
  const loadStats = async () => {
    try {
      // 获取30天的数据，让TrendChart组件在前端进行筛选
      const data = await getMonitorStats(productId, 30);
      setStats(data);
    } catch (error) {
      console.error('加载统计数据失败:', (error as Error).message);
      setError((error as Error).message);
    }
  };

  // 加载产品详情信息（包含SKU）- 从监控记录中获取
  const loadProductDetail = async () => {
    try {
      setSkuLoading(true);

      
      // 从监控记录中获取最新记录，包含SKU信息
      const records = await getProductMonitorRecords(productId, 1); // 只获取最新一条记录
      
      if (records && records.length > 0 && records[0].sku_info) {
        const latestRecord = records[0];
        const skuInfo = latestRecord.sku_info;
        
        if (Array.isArray(skuInfo) && skuInfo.length > 0) {
          const productDetailInfo: ProductDetailInfo = {
            productId,
            productTitle: productName,
            productImage,
            shopName,
            shopLogo,
            salesCount: latestRecord.sales_count || 0,
            reviewCount: latestRecord.review_count || 0,
            productRating: latestRecord.product_rating,
            skus: skuInfo,
            selectedSku: skuInfo[0] // 默认选择第一个SKU
          };
          
          setProductDetail(productDetailInfo);
          setSelectedSku(skuInfo[0]);
          

          return;
        }
      }
      

      // 如果监控记录中没有SKU信息，创建默认SKU
      const defaultSku: SkuInfo = {
        sku_id: 'default',
        sku_name: '默认规格',
        price: 0,
        currency: 'USD'
      };
      
      const productDetailInfo: ProductDetailInfo = {
        productId,
        productTitle: productName,
        productImage,
        shopName,
        shopLogo,
        salesCount: 0,
        reviewCount: 0,
        skus: [defaultSku],
        selectedSku: defaultSku
      };
      
      setProductDetail(productDetailInfo);
      setSelectedSku(defaultSku);
      
    } catch (error) {
      console.error('加载产品详情失败:', (error as Error).message);
      // 创建默认产品详情
      const defaultSku: SkuInfo = {
        sku_id: 'default',
        sku_name: '默认规格',
        price: 0,
        currency: 'USD'
      };
      
      const productDetailInfo: ProductDetailInfo = {
        productId,
        productTitle: productName,
        productImage,
        shopName,
        shopLogo,
        salesCount: 0,
        reviewCount: 0,
        skus: [defaultSku],
        selectedSku: defaultSku
      };
      
      setProductDetail(productDetailInfo);
      setSelectedSku(defaultSku);
    } finally {
      setSkuLoading(false);
    }
  };

  // SKU选择处理
  const handleSkuChange = (sku: SkuInfo) => {
    setSelectedSku(sku);
  };

  // SKU拖拽滚动处理函数
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    const element = e.currentTarget;
    setDragStart({
      x: e.pageX - element.offsetLeft,
      scrollLeft: element.scrollLeft
    });
    element.style.cursor = 'grabbing';
    element.style.userSelect = 'none';
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(false);
    e.currentTarget.style.cursor = 'grab';
    e.currentTarget.style.userSelect = 'auto';
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(false);
    e.currentTarget.style.cursor = 'grab';
    e.currentTarget.style.userSelect = 'auto';
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    e.preventDefault();
    const element = e.currentTarget;
    const x = e.pageX - element.offsetLeft;
    const walk = (x - dragStart.x) * 2; // 滚动速度倍数
    element.scrollLeft = dragStart.scrollLeft - walk;
  };

  // 格式化价格显示
  const formatPrice = (price: number, currency: string = 'USD'): string => {
    const currencySymbols: { [key: string]: string } = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CNY': '¥',
      'SGD': 'S$',
      'MYR': 'RM',
      'IDR': 'Rp',
      'PHP': '₱',
      'THB': '฿',
      'VND': '₫'
    };
    
    const symbol = currencySymbols[currency] || currency;
    
    // 根据货币类型决定小数位数
    const decimals = ['JPY', 'VND', 'IDR'].includes(currency) ? 0 : 2;
    
    return `${symbol}${price.toFixed(decimals)}`;
  };

  // 计算价格范围显示
  const getPriceRange = (): string => {
    if (!productDetail || !productDetail.skus || productDetail.skus.length === 0) {
      return '';
    }

    // 筛选出有效价格的SKU，并获取对应的货币信息
    const validSkus = productDetail.skus.filter(sku => 
      typeof sku.price === 'number' && !isNaN(sku.price) && sku.price >= 0
    );

    if (validSkus.length === 0) {
      return '';
    }

    // 如果只有一个有效SKU，直接返回单个价格
    if (validSkus.length === 1) {
      return formatPrice(validSkus[0].price, validSkus[0].currency || 'USD');
    }

    // 计算最低价和最高价
    const prices = validSkus.map(sku => sku.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    // 获取货币信息（使用第一个有效SKU的货币）
    const currency = validSkus[0].currency || 'USD';

    // 如果最低价和最高价相同，只显示一个价格
    if (minPrice === maxPrice) {
      return formatPrice(minPrice, currency);
    }

    // 返回价格范围，格式：$1.00 ~ $7.00
    return `${formatPrice(minPrice, currency)} ~ ${formatPrice(maxPrice, currency)}`;
  };

  // 获取完整历史记录
  const loadAllRecords = async () => {
    try {
      const response = await fetch(`/api/monitor/products/${productId}/records?limit=1000`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        const records = result.data || [];
        setAllRecords(records);
        
        // 默认所有日期分组都为折叠状态
        setExpandedDates(new Set());
      } else {
        throw new Error(result.message || '获取记录失败');
      }
    } catch (error) {
      console.error('获取历史记录失败:', (error as Error).message);
      setAllRecords([]);
    }
  };

  // 计算记录的增量数据（全局计算，而不是按日期分组）
  const calculateGlobalIncrements = (allRecords: any[]) => {
    // 按时间排序（最新的在前）
    const sortedRecords = [...allRecords].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    
    return sortedRecords.map((record, index) => {
      const nextRecord = sortedRecords[index + 1];
      let sales_increment = 0;
      let review_increment = 0;
      
      if (nextRecord) {
        // 允许负增量（减量），不强制设为0
        sales_increment = record.sales_count - nextRecord.sales_count;
        review_increment = record.review_count - nextRecord.review_count;
      }
      
      return {
        ...record,
        sales_increment,
        review_increment
      };
    });
  };

  // 按日期分组记录并应用全局增量计算
  const groupRecordsByDateWithIncrements = (records: any[]) => {
    // 先进行全局增量计算
    const recordsWithIncrements = calculateGlobalIncrements(records);
    
    // 再按日期分组
    const groups = new Map<string, any[]>();
    
    for (const record of recordsWithIncrements) {
      // 直接从时间字符串提取日期部分，避免时区转换问题
      const date = record.created_at.split(' ')[0]; // 格式: "2025-06-18 17:02:58" -> "2025-06-18"
      if (!groups.has(date)) {
        groups.set(date, []);
      }
      groups.get(date)!.push(record);
    }
    
    // 按日期排序（最新的在前）并对每日记录按时间排序
    const sortedGroups = Array.from(groups.entries())
      .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
      .map(([date, records]) => [
        date,
        records.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      ]);
    
    const result = new Map(sortedGroups as [string, any[]][]);
    return result;
  };

  // 移除旧的按日期分组和增量计算函数
  const groupRecordsByDate = groupRecordsByDateWithIncrements;

  // 移除旧的calculateRecordIncrements函数，直接使用已计算好的增量数据
  const calculateRecordIncrements = (records: any[]) => records; // 直接返回，因为增量已经在全局计算了

  // 切换日期组的展开/折叠状态
  const toggleDateGroup = (date: string) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  // 格式化日期显示（使用中国时区）
  const formatDateDisplay = (dateStr: string) => {
    return getFriendlyDateDisplay(dateStr) === '今天' || getFriendlyDateDisplay(dateStr) === '昨天' 
      ? `${getFriendlyDateDisplay(dateStr)} (${dateStr})`
      : dateStr;
  };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // 并行加载统计数据、产品详情和历史记录
        await Promise.all([
          loadStats(),
          loadProductDetail(),
          loadAllRecords()
        ]);
      } catch (error) {
        console.error('初始化数据失败:', (error as Error).message);
        setError('加载数据失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };
    
    initializeData();
  }, [productId]);

  // 手动刷新 - 动态刷新数据，不进入加载状态
  const handleRefresh = async () => {
    try {
      setRefreshing(true); // 使用独立的刷新状态
      
      // 显示开始提示
      const startToast = document.createElement('div');
      startToast.className = 'toast toast-top toast-end';
      startToast.innerHTML = `
        <div class="alert alert-info">
          <span>🚀 正在更新产品监控数据...</span>
        </div>
      `;
      document.body.appendChild(startToast);
      setTimeout(() => {
        if (document.body.contains(startToast)) {
          document.body.removeChild(startToast);
        }
      }, 3000);

      // 先触发后端监控更新
      const monitorResponse = await fetch(`/api/monitor/products/${productId}/monitor`, {
        method: 'POST',
      });
      const monitorResult = await monitorResponse.json();

      if (monitorResult.success) {
        // 监控成功，等待一小段时间让数据写入
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // 重新加载统计数据和历史记录
        await Promise.all([loadStats(), loadAllRecords()]);
        
        // 显示成功提示
        const successToast = document.createElement('div');
        successToast.className = 'toast toast-top toast-end';
        successToast.innerHTML = `
          <div class="alert alert-success">
            <span>✅ 监控数据更新成功</span>
          </div>
        `;
        document.body.appendChild(successToast);
        setTimeout(() => {
          if (document.body.contains(successToast)) {
            document.body.removeChild(successToast);
          }
        }, 3000);
      } else {
        throw new Error(monitorResult.message || '监控更新失败');
      }
    } catch (error) {
              console.error('手动更新失败:', (error as Error).message);
      
      // 尝试只重新加载现有数据
      await Promise.all([loadStats(), loadAllRecords()]);
      
      // 显示错误提示
      const errorToast = document.createElement('div');
      errorToast.className = 'toast toast-top toast-end';
      errorToast.innerHTML = `
        <div class="alert alert-warning">
          <span>⚠️ 监控更新失败，已重新加载现有数据</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 5000);
    } finally {
      setRefreshing(false); // 重置刷新状态
    }
  };

  // 优化数据 - 删除无效记录
  const handleOptimizeData = async () => {
    try {
      setOptimizing(true);
      
      // 显示开始提示
      const startToast = document.createElement('div');
      startToast.className = 'toast toast-top toast-end';
      startToast.innerHTML = `
        <div class="alert alert-info">
          <span>🔧 正在优化数据，删除无效记录...</span>
        </div>
      `;
      document.body.appendChild(startToast);
      setTimeout(() => {
        if (document.body.contains(startToast)) {
          document.body.removeChild(startToast);
        }
      }, 3000);

      // 调用优化API
      const response = await fetch(`/api/monitor/products/${productId}/optimize`, {
        method: 'POST',
      });
      const result = await response.json();

      if (result.success) {
        // 优化成功，重新加载数据
        await Promise.all([loadStats(), loadAllRecords()]);
        
        // 显示成功提示
        const successToast = document.createElement('div');
        successToast.className = 'toast toast-top toast-end';
        successToast.innerHTML = `
          <div class="alert alert-success">
            <span>✅ 数据优化完成！删除了 ${result.data.deletedCount} 条无效记录</span>
          </div>
        `;
        document.body.appendChild(successToast);
        setTimeout(() => {
          if (document.body.contains(successToast)) {
            document.body.removeChild(successToast);
          }
        }, 5000);
      } else {
        throw new Error(result.message || '数据优化失败');
      }
    } catch (error) {
              console.error('数据优化失败:', (error as Error).message);
      
      // 显示错误提示
      const errorToast = document.createElement('div');
      errorToast.className = 'toast toast-top toast-end';
      errorToast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ 数据优化失败: ${error instanceof Error ? error.message : '未知错误'}</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 5000);
    } finally {
      setOptimizing(false);
    }
  };

  // 检测数据异常
  const detectDataAnomaly = (record: any, previousRecord: any): { 
    hasSalesAnomaly: boolean; 
    hasReviewAnomaly: boolean; 
    anomalyReasons: string[] 
  } => {
    const anomalyReasons: string[] = [];
    let hasSalesAnomaly = false;
    let hasReviewAnomaly = false;

    if (previousRecord) {
      // 计算下降比例，用于判断是否为异常下降
      const salesDecrease = previousRecord.sales_count - record.sales_count;
      const reviewDecrease = previousRecord.review_count - record.review_count;
      
      // 检测销量异常大幅下降（下降超过10%或绝对值超过100，认为异常）
      if (salesDecrease > 0) {
        const decreasePercent = (salesDecrease / previousRecord.sales_count) * 100;
        if (decreasePercent > 10 || salesDecrease > 100) {
          hasSalesAnomaly = true;
          anomalyReasons.push(`销量大幅下降 ${salesDecrease}（${decreasePercent.toFixed(1)}%）`);
        }
      }

      // 检测评论异常大幅下降（下降超过20%或绝对值超过50，认为异常）
      if (reviewDecrease > 0 && previousRecord.review_count > 0) {
        const decreasePercent = (reviewDecrease / previousRecord.review_count) * 100;
        if (decreasePercent > 20 || reviewDecrease > 50) {
          hasReviewAnomaly = true;
          anomalyReasons.push(`评论大幅下降 ${reviewDecrease}（${decreasePercent.toFixed(1)}%）`);
        }
      }

      // 检测数据异常归零（从有数据突然变为0）
      if (record.review_count === 0 && previousRecord.review_count > 10) {
        hasReviewAnomaly = true;
        anomalyReasons.push('评论数据异常归零');
      }

      if (record.sales_count === 0 && previousRecord.sales_count > 10) {
        hasSalesAnomaly = true;
        anomalyReasons.push('销量数据异常归零');
      }
    }

    // 检测数据完整性
    if (record.sales_count === null || record.sales_count === undefined) {
      hasSalesAnomaly = true;
      anomalyReasons.push('销量数据缺失');
    }

    if (record.review_count === null || record.review_count === undefined) {
      hasReviewAnomaly = true;
      anomalyReasons.push('评论数据缺失');
    }

    return { hasSalesAnomaly, hasReviewAnomaly, anomalyReasons };
  };

  // 删除单条记录
  const handleDeleteRecord = async (recordId: string, recordTime: string) => {
    const confirmed = confirm(`确定要删除记录 "${recordTime}" 吗？\n\n⚠️ 删除后将重新计算增量数据，此操作不可撤销。`);
    if (!confirmed) return;

    try {
      // 调用删除API
      const response = await fetch(`/api/monitor/records/${recordId}`, {
        method: 'DELETE',
      });
      const result = await response.json();

      if (result.success) {
        // 重新加载数据
        await Promise.all([loadStats(), loadAllRecords()]);
        
        // 显示成功提示
        const successToast = document.createElement('div');
        successToast.className = 'toast toast-top toast-end';
        successToast.innerHTML = `
          <div class="alert alert-success">
            <span>✅ 记录已删除，增量数据已重新计算</span>
          </div>
        `;
        document.body.appendChild(successToast);
        setTimeout(() => {
          if (document.body.contains(successToast)) {
            document.body.removeChild(successToast);
          }
        }, 3000);
      } else {
        throw new Error(result.message || '删除失败');
      }
    } catch (error) {
      console.error('删除记录失败:', error);
      
      // 显示错误提示
      const errorToast = document.createElement('div');
      errorToast.className = 'toast toast-top toast-end';
      errorToast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ 删除失败: ${error instanceof Error ? error.message : '未知错误'}</span>
        </div>
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => {
        if (document.body.contains(errorToast)) {
          document.body.removeChild(errorToast);
        }
      }, 5000);
    }
  };

  if (loading) {
    return (
      <div className="modal modal-open">
        <div className="modal-box max-w-7xl h-[95vh] max-h-[95vh] my-2" data-modal-type="loading">
          <div className="flex items-center justify-center h-full">
            <div className="loading loading-spinner loading-lg"></div>
            <span className="ml-3">加载监控数据中...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal modal-open">
        <div className="modal-box max-w-7xl" data-modal-type="error">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">❌</div>
            <h3 className="text-xl font-bold text-error mb-2">加载失败</h3>
            <p className="text-base-content/60 mb-4">{error}</p>
            <div className="space-x-2">
              <button className="btn btn-primary" onClick={handleRefresh}>
                重试
              </button>
              <button className="btn btn-ghost" onClick={onClose}>
                关闭
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="modal modal-open">
        <div className="modal-box max-w-7xl" data-modal-type="no-data">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-bold text-base-content/80 mb-2">暂无数据</h3>
            <p className="text-base-content/60">该产品还没有监控数据</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal modal-open">
      <div 
        ref={modalBoxRef}
        className="modal-box max-w-7xl h-[95vh] max-h-[95vh] my-2 overflow-hidden flex flex-col bg-gradient-to-br from-base-100 to-base-200"
        data-modal-type="product-detail"
      >
        {/* 刷新进度条 */}
        {refreshing && (
          <div className="fixed top-0 left-0 right-0 z-50">
            <div className="h-1 w-full bg-gradient-to-r from-primary/20 via-primary to-primary/20 animate-pulse"></div>
          </div>
        )}

        {/* 头部 */}
        <div className="flex items-center justify-between mb-6 border-b border-base-300 pb-4 bg-base-100/50 -mx-6 -mt-6 px-6 pt-6 backdrop-blur-sm">
          <div className="flex items-center justify-between w-full">
            {/* 左侧：产品信息 */}
            <div className="flex items-start space-x-3 flex-1 min-w-0 h-24">
              {/* 产品图片 - 1:1比例，占满容器高度 */}
              <div className="w-24 h-24 flex-shrink-0">
                <img 
                  src={productImage || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxyZWN0IHg9IjQ4IiB5PSI0OCIgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiByeD0iNCIgZmlsbD0iIzlDQTNBRiIvPgo8c3ZnIHg9IjU2IiB5PSI1NiIgd2lkdGg9IjE2IiBoZWlnaHQ9IjE2IiB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9IiNGRkZGRkYiPgo8cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptLTIgMTVsLTUtNSAxLjQxLTEuNDFMMTAgMTQuMTdsNy4yOS03LjU5TDE5IDhsLTkgOXoiLz4KPC9zdmc+Cjwvc3ZnPg=='} 
                  alt={productName || `产品 ${productId}`}
                  className="w-full h-full rounded-xl object-cover ring-2 ring-blue-500/20 shadow-lg"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjRkVGMUYxIi8+CjxwYXRoIGQ9Ik02NCA0OEM1Ni4yIDQ4IDUwIDU0LjIgNTAgNjJTNTYuMiA3NiA2NCA3NlM3OCA2OS44IDc4IDYyUzcxLjggNDggNjQgNDhaIiBmaWxsPSIjRUY0NDQ0Ii8+CjxwYXRoIGQ9Ik01NiA1Nkw3MiA3Mk01NiA3Mkw3MiA1NiIgc3Ryb2tlPSIjRkZGRkZGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8L3N2Zz4=';
                  }}
                />
              </div>
                              <div className="min-w-0 flex-1 space-y-3">
                  {/* 上半部分：产品名称和价格 */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      {/* 产品名称 - 单行显示，不换行 */}
                      <h2 
                        className="text-2xl font-bold text-base-content truncate mb-2"
                        title={productName || `产品 ${productId}`}
                      >
                        {productName || `产品 ${productId}`}
                      </h2>
                      
                      {/* 产品ID和功能按钮 */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                          <p className="text-sm text-base-content/60 font-mono">ID: {productId}</p>
                        </div>
                        
                        {/* 功能按钮组 */}
                        <div className="flex items-center gap-1">
                          <button
                            className="btn btn-xs btn-ghost gap-1 text-blue-600 hover:bg-blue-50"
                            onClick={handleCopyId}
                            title="复制产品ID"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            复制ID
                          </button>
                          
                          <button
                            className="btn btn-xs btn-ghost gap-1 text-green-600 hover:bg-green-50"
                            onClick={handleCopyLink}
                            title="复制产品链接"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            复制链接
                          </button>
                          
                          <button
                            className="btn btn-xs btn-ghost gap-1 text-purple-600 hover:bg-purple-50"
                            onClick={handleOpenLink}
                            title="在新窗口打开产品链接"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            打开链接
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* 价格显示区域 - 扩展为两行，更大字体 */}
                    <div className="text-right flex-shrink-0 min-w-[140px]">
                      {skuLoading ? (
                        <div className="flex flex-col items-end gap-2">
                          <span className="loading loading-spinner loading-md"></span>
                          <span className="text-sm text-base-content/60">加载中...</span>
                        </div>
                      ) : selectedSku && selectedSku.price !== undefined && selectedSku.price >= 0 ? (
                        <div className="space-y-1">
                          {/* 当前价格 - 更大字体 */}
                          <div className="text-3xl font-bold text-primary">
                            {formatPrice(selectedSku.price, selectedSku.currency)}
                          </div>
                          
                          {/* 原价（如果有折扣） */}
                          {selectedSku.original_price && selectedSku.original_price > selectedSku.price && (
                            <div className="text-base text-base-content/60 line-through">
                              {formatPrice(selectedSku.original_price, selectedSku.currency)}
                            </div>
                          )}
                          
                          {/* 价格范围（当有多个SKU时显示） */}
                          {productDetail && productDetail.skus.length > 1 && (
                            <div className="text-xs text-primary/70 font-medium w-full text-right" title={`价格范围: ${getPriceRange()}`}>
                              {getPriceRange()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-base-content/40">
                          价格信息加载中...
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 下半部分：SKU选择区域 - 跨越整个宽度 */}
                  {productDetail && productDetail.skus && productDetail.skus.length > 1 && (
                    <div className="flex items-center gap-2 w-full">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <label className="text-xs text-base-content/60 font-medium whitespace-nowrap">
                          SKU[{productDetail.skus.length}个选项]:
                        </label>
                      </div>
                      <div 
                        className="flex gap-1 overflow-x-auto flex-1"
                        style={{
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                          cursor: 'grab'
                        } as React.CSSProperties}
                        onMouseDown={handleMouseDown}
                        onMouseLeave={handleMouseLeave}
                        onMouseUp={handleMouseUp}
                        onMouseMove={handleMouseMove}
                      >
                        {productDetail.skus.map((sku) => (
                          <button
                            key={sku.sku_id}
                            className={`btn btn-xs whitespace-nowrap flex-shrink-0 ${
                              selectedSku?.sku_id === sku.sku_id
                                ? 'btn-primary'
                                : 'btn-outline btn-primary'
                            }`}
                            onClick={() => handleSkuChange(sku)}
                            disabled={skuLoading}
                          >
                            {sku.sku_name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
            </div>

            {/* 分割线 - 使用绝对定位确保显示 */}
            <div className="relative flex-shrink-0 w-4 h-24 mx-2">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 transform -translate-x-1/2"></div>
            </div>

            {/* 右侧：店铺信息和关闭按钮 */}
            <div className="flex items-center space-x-4 flex-shrink-0 ml-6">
              {/* 店铺信息 - 垂直布局，头像在上，名字在下 */}
              <div className="flex flex-col items-center text-center space-y-2 min-w-[120px]">
                <img 
                  src={shopLogo} 
                  alt={shopName}
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-primary/20"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxjaXJjbGUgY3g9IjUwIiBjeT0iNTAiIHI9IjUwIiBmaWxsPSIjRERENkZFIi8+CjxwYXRoIGQ9Ik01MCAyNUM0MS43IDI1IDM1IDMxLjcgMzUgNDBDMzUgNDguMyA0MS43IDU1IDUwIDU1QzU4LjMgNTUgNjUgNDguMyA2NSA0MEM2NSAzMS43IDU4LjMgMjUgNTAgMjVaTTUwIDc1QzQyLjUgNzUgMzUuNjIgNzEuMjUgMzEuMjUgNjUuNjJDMzEuMjUgNTkuMzcgNDMuNzUgNTYuMjUgNTAgNTYuMjVDNTYuMjUgNTYuMjUgNjguNzUgNTkuMzcgNjguNzUgNjUuNjJDNjQuMzggNzEuMjUgNTcuNSA3NSA1MCA3NVoiIGZpbGw9IiM4QjVDRjYiLz4KPC9zdmc+';
                  }}
                />
                <h3 className="text-xs font-medium text-base-content w-full truncate px-1" title={shopName}>
                  {shopName}
                </h3>
              </div>
              
              {/* 按钮组 - 垂直布局，居中对齐 */}
              <div className="flex flex-col items-center gap-2">
                {/* 关闭按钮 */}
                  <button
                  className="btn btn-sm btn-circle btn-ghost"
                  onClick={onClose}
                >
                  ✕
                </button>
                
                {/* 截图按钮 - 直接复制到剪贴板 */}
                <button
                  onClick={() => captureProductDetail('copy')}
                    className="btn btn-sm btn-square btn-ghost tooltip tooltip-left"
                    data-tip="截图"
                    disabled={isCapturing}
                  >
                  <div className="flex items-center justify-center w-full h-full">
                    {isCapturing ? (
                      <span className="loading loading-spinner loading-sm"></span>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
              </div>
              </button>
              </div>
            </div>
          </div>
        </div>

        {/* 内容区域 - 可滚动 */}
        <div className="flex-1 overflow-y-auto space-y-6">
          {/* 总览卡片 - 添加card容器确保与下面卡片对齐 */}
          <div className="card">
            <div className="card-body">
              <div className="grid grid-cols-3 gap-4">
                {/* 当前总销量 */}
                <div className="stat bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between h-full">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500 rounded-lg text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="stat-title text-blue-600 text-sm font-medium">当前总销量</div>
                        <div className="stat-desc text-blue-600/70 text-xs">最新监控数据</div>
                      </div>
                    </div>
                    <div className="stat-value text-blue-600 text-xl font-bold">
                      {(() => {
                        // 从历史记录中获取最新的销量数据
                        if (allRecords.length === 0) return stats.latest_sales?.toLocaleString('zh-CN') || '0';
                        const latestRecord = allRecords[0]; // allRecords已按时间倒序排列，第一个是最新的
                        return latestRecord.sales_count?.toLocaleString('zh-CN') || '0';
                      })()}
                    </div>
                  </div>
                </div>

                {/* 当前总评论 */}
                <div className="stat bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between h-full">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-500 rounded-lg text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div>
                        <div className="stat-title text-green-600 text-sm font-medium">当前总评论</div>
                        <div className="stat-desc text-green-600/70 text-xs">用户评论数</div>
                      </div>
                    </div>
                    <div className="stat-value text-green-600 text-xl font-bold">
                      {(() => {
                        // 从历史记录中获取最新的评论数据
                        if (allRecords.length === 0) return stats.latest_reviews?.toLocaleString('zh-CN') || '0';
                        const latestRecord = allRecords[0]; // allRecords已按时间倒序排列，第一个是最新的
                        return latestRecord.review_count?.toLocaleString('zh-CN') || '0';
                      })()}
                    </div>
                  </div>
                </div>

                {/* 当前评分 */}
                <div className="stat bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between h-full">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-500 rounded-lg text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </div>
                      <div>
                        <div className="stat-title text-yellow-600 text-sm font-medium">当前评分</div>
                        <div className="stat-desc text-yellow-600/70 text-xs">产品评分</div>
                      </div>
                    </div>
                    <div className="stat-value text-yellow-600 text-xl font-bold">
                      {(() => {
                        // 从历史记录中获取最新的评分数据
                        if (allRecords.length === 0) return stats.latest_rating?.toFixed(1) || '0.0';
                        const latestRecord = allRecords[0]; // allRecords已按时间倒序排列，第一个是最新的
                        return latestRecord.product_rating?.toFixed(1) || '0.0';
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 今日增量趋势 */}
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex justify-between items-center mb-4">
                <h3 className="card-title">
                  <span className="text-accent">📈</span>
                  今日增量趋势
                  {refreshing && (
                    <span className="loading loading-dots loading-sm text-primary ml-2"></span>
                  )}
                </h3>
                <div className="flex items-center gap-3">
                  {/* 记录统计标签 */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-accent/10 rounded-lg border border-accent/20">
                    <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <span className="text-accent font-semibold text-sm">
                      {stats.today_increments.length} 条记录
                    </span>
                  </div>
                  
                  {/* 操作按钮组 */}
                  <div className="flex items-center gap-2">
                    {/* 立即更新按钮 - 移动到这里 */}
                    <button
                      className="btn btn-sm btn-primary gap-2 hover:btn-primary transition-all duration-200"
                      onClick={handleRefresh}
                      disabled={refreshing}
                      title="手动触发产品监控更新"
                    >
                      {refreshing ? (
                        <span className="loading loading-spinner loading-sm"></span>
                      ) : (
                        <span className={`text-sm ${refreshing ? 'animate-spin' : ''}`}>🔄</span>
                      )}
                      <span className="hidden sm:inline">
                        {refreshing ? '更新中...' : '立即更新'}
                      </span>
                    </button>
                    
                    <button
                      className="btn btn-sm btn-warning btn-outline gap-2 hover:btn-warning transition-all duration-200"
                      onClick={handleOptimizeData}
                      disabled={optimizing || refreshing}
                      title="删除既没有销量增量也没有评论增量的无效记录"
                    >
                      {optimizing ? (
                        <span className="loading loading-spinner loading-sm"></span>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      )}
                      <span className="hidden sm:inline">
                        {optimizing ? '优化中...' : '优化数据'}
                      </span>
                    </button>
                    
                    <button
                      className={`btn btn-sm gap-2 transition-all duration-200 border-2 ${
                        showChart 
                          ? 'btn-error border-error hover:bg-error hover:text-white bg-error/10 text-error' 
                          : 'btn-success border-success hover:bg-success hover:text-white bg-success/10 text-success'
                      }`}
                      onClick={() => setShowChart(!showChart)}
                      title={showChart ? '隐藏图表' : '显示图表'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {showChart ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        )}
                      </svg>
                      <span className="hidden sm:inline font-semibold">
                        {showChart ? '隐藏图表' : '显示图表'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* 今日增量汇总 */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="stat bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center justify-between h-full">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-500 rounded-lg text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <div>
                        <div className="stat-title text-emerald-600 text-sm font-medium">今日销量增量</div>
                        <div className="stat-desc text-emerald-600/70 text-xs">真实增量累计</div>
                      </div>
                    </div>
                    <div className="stat-value text-emerald-600 text-xl font-bold">
                      +{(() => {
                        // 计算今日的销量增量
                        if (allRecords.length === 0) return '0';
                        const groupedData = groupRecordsByDate(allRecords);
                        const today = getChinaDate();
                        const todayRecords = groupedData.get(today) || [];
                        return todayRecords.reduce((sum, r) => sum + r.sales_increment, 0).toLocaleString('zh-CN');
                      })()}
                    </div>
                  </div>
                </div>
                
                <div className="stat bg-gradient-to-r from-cyan-50 to-cyan-100 border border-cyan-200 rounded-lg p-4">
                  <div className="flex items-center justify-between h-full">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-cyan-500 rounded-lg text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10m0 0V18a2 2 0 01-2 2H9a2 2 0 01-2-2V8m8 0V6a2 2 0 00-2-2H9a2 2 0 00-2 2v2m8 0H7" />
                        </svg>
                      </div>
                      <div>
                        <div className="stat-title text-cyan-600 text-sm font-medium">今日评论增量</div>
                        <div className="stat-desc text-cyan-600/70 text-xs">真实增量累计</div>
                      </div>
                    </div>
                    <div className="stat-value text-cyan-600 text-xl font-bold">
                      +{(() => {
                        // 计算今日的评论增量
                        if (allRecords.length === 0) return '0';
                        const groupedData = groupRecordsByDate(allRecords);
                        const today = getChinaDate();
                        const todayRecords = groupedData.get(today) || [];
                        return todayRecords.reduce((sum, r) => sum + r.review_increment, 0).toLocaleString('zh-CN');
                      })()}
                    </div>
                  </div>
                </div>
                
                <div className="stat bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center justify-between h-full">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-500 rounded-lg text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="stat-title text-amber-600 text-sm font-medium">监控频次</div>
                        <div className="stat-desc text-amber-600/70 text-xs">
                          {stats.today_increments.length > 0 ? '含基准数据' : '次数据更新'}
                        </div>
                      </div>
                    </div>
                    <div className="stat-value text-amber-600 text-xl font-bold">
                      {stats.today_increments.length}
                    </div>
                  </div>
                </div>
              </div>

              {/* 图表展示 */}
              {showChart && stats.daily_trend && stats.daily_trend.length > 0 && (
                <div className="mb-6 space-y-6">
                  {/* 今日分时销量趋势图 */}
                  <HourlyTrendChart
                    data={allRecords}
                    height={250}
                    title="今日分时销量走势"
                    productData={productDetail}
                  />
                  
                  {/* 销量与评论增量趋势（包含评分趋势） */}
                  <TrendChart 
                    data={stats.daily_trend} 
                    title="销量与评论增量趋势" 
                    height={300}
                    showRating={true}
                  />
                </div>
              )}

              {/* 详细记录表格 - 按日期分组 */}
              {allRecords.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="text-lg font-semibold text-base-content mb-4">
                    📋 历史记录详情
                    <span className="text-sm text-base-content/60 font-normal ml-2">
                      ({allRecords.length} 条记录)
                    </span>
                    {refreshing && (
                      <span className="loading loading-dots loading-sm text-primary ml-2"></span>
                    )}
                  </h4>
                  
                  {Array.from(groupRecordsByDate(allRecords)).map(([date, dateRecords]: [string, any[]]) => {
                    const isExpanded = expandedDates.has(date);
                    const recordsWithIncrements = calculateRecordIncrements(dateRecords);
                    const isToday = date === getChinaDate();
                    
                    return (
                      <div key={date} className="border border-base-300 rounded-lg overflow-hidden">
                        {/* 日期组头部 */}
                        <div 
                          className={`flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors ${
                            isToday ? 'bg-accent/10 hover:bg-accent/20' : 'bg-base-200 hover:bg-base-300'
                          }`}
                          onClick={() => toggleDateGroup(date)}
                        >
                          {/* 左侧信息 */}
                          <div className="flex items-center gap-2.5 flex-wrap">
                            {/* 展开/折叠图标 */}
                            <div className={`transition-transform duration-200 text-base ${isExpanded ? 'rotate-90' : ''}`}>
                              ▶️
                            </div>
                            
                            {/* 日期信息 */}
                            <div className="font-semibold text-base-content text-sm">
                              {formatDateDisplay(date)}
                            </div>
                            
                            {/* 今日标签 */}
                            {isToday && (
                              <span className="badge badge-accent badge-xs">今日</span>
                            )}
                            
                            {/* 分隔符 - 在小屏幕上隐藏 */}
                            <div className="w-px h-3 bg-base-300/60 hidden sm:block"></div>
                            
                            {/* 记录数 */}
                            <div className="text-xs text-base-content/70">
                              {recordsWithIncrements.length} 条记录
                            </div>
                            
                            {/* 分隔符 - 在小屏幕上隐藏 */}
                            <div className="w-px h-3 bg-base-300/60 hidden md:block"></div>
                            
                            {/* 销量增量 */}
                            <div className="text-xs flex items-center gap-1">
                              <span className="text-base-content/70 hidden sm:inline">📈</span>
                              <span className="text-success font-semibold">
                                +{recordsWithIncrements.reduce((sum, r) => sum + r.sales_increment, 0).toLocaleString('zh-CN')}
                              </span>
                            </div>
                            
                            {/* 分隔符 - 在小屏幕上隐藏 */}
                            <div className="w-px h-3 bg-base-300/60 hidden md:block"></div>
                            
                            {/* 评论增量 */}
                            <div className="text-xs flex items-center gap-1">
                              <span className="text-base-content/70 hidden sm:inline">💬</span>
                              <span className="text-info font-semibold">
                                +{recordsWithIncrements.reduce((sum, r) => sum + r.review_increment, 0).toLocaleString('zh-CN')}
                              </span>
                            </div>
                          </div>
                          
                          {/* 右侧折叠状态图标 */}
                          <div className="text-base">
                            {isExpanded ? '📂' : '📁'}
                          </div>
                        </div>
                        
                        {/* 日期组内容 */}
                        {isExpanded && (
                          <div className="overflow-x-auto">
                            <table className="table table-zebra w-full min-w-[800px]">
                              <thead>
                                <tr className={`${
                                  isToday 
                                    ? 'bg-gradient-to-r from-red-100 to-red-200 border-b-2 border-red-300 shadow-sm' 
                                    : 'bg-gradient-to-r from-gray-100 to-gray-200 border-b-2 border-gray-300 shadow-sm'
                                }`}>
                                  <th className={`text-sm font-semibold py-3 w-40 ${isToday ? 'text-red-800' : 'text-gray-700'}`}>
                                    <div className="flex items-center justify-center gap-1.5">
                                      <span className="text-xs">🕒</span>
                                      <span>时间</span>
                                    </div>
                                  </th>
                                  <th className={`text-sm font-semibold py-3 w-44 ${isToday ? 'text-red-800' : 'text-gray-700'}`}>
                                    <div className="flex items-center justify-center gap-1.5">
                                      <span className="text-xs">🌍</span>
                                      <span>当地时间</span>
                                      <span className="text-xs opacity-70">({getProductRegion()})</span>
                                    </div>
                                  </th>
                                  <th className={`text-sm font-semibold py-3 w-24 ${isToday ? 'text-red-800' : 'text-gray-700'}`}>
                                    <div className="flex items-center justify-center gap-1.5">
                                      <span className="text-xs">📈</span>
                                      <span>销量增量</span>
                                    </div>
                                  </th>
                                  <th className={`text-sm font-semibold py-3 w-24 ${isToday ? 'text-red-800' : 'text-gray-700'}`}>
                                    <div className="flex items-center justify-center gap-1.5">
                                      <span className="text-xs">💬</span>
                                      <span>评论增量</span>
                                    </div>
                                  </th>
                                  <th className={`text-sm font-semibold py-3 w-28 ${isToday ? 'text-red-800' : 'text-gray-700'}`}>
                                    <div className="flex items-center justify-center gap-1.5">
                                      <span className="text-xs">🛒</span>
                                      <span>累计销量</span>
                                    </div>
                                  </th>
                                  <th className={`text-sm font-semibold py-3 w-28 ${isToday ? 'text-red-800' : 'text-gray-700'}`}>
                                    <div className="flex items-center justify-center gap-1.5">
                                      <span className="text-xs">💭</span>
                                      <span>累计评论</span>
                                    </div>
                                  </th>
                                  <th className={`text-sm font-semibold py-3 w-20 ${isToday ? 'text-red-800' : 'text-gray-700'}`}>
                                    <div className="flex items-center justify-center gap-1.5">
                                      <span className="text-xs">⭐</span>
                                      <span>评分</span>
                                    </div>
                                  </th>
                                  <th className={`text-sm font-semibold py-3 w-20 ${isToday ? 'text-red-800' : 'text-gray-700'}`}>
                                    <div className="flex items-center justify-center gap-1.5">
                                      <span className="text-xs">⏱️</span>
                                      <span>间隔时间</span>
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {recordsWithIncrements.map((record, index) => {
                                  // 判断是否为基准数据：只有全局最后一条记录且增量为0才是基准数据
                                  const globalIndex = allRecords.findIndex(r => r.created_at === record.created_at);
                                  const isBaselineData = globalIndex === allRecords.length - 1 && 
                                                       record.sales_increment === 0 && 
                                                       record.review_increment === 0;
                                  
                                  const previousRecord = recordsWithIncrements[index + 1];
                                  const isAuto = previousRecord ? isAutoMonitoring(record.created_at, previousRecord.created_at) : false;
                                  const timeInterval = previousRecord ? calculateTimeInterval(record.created_at, previousRecord.created_at) : '-';
                                  const productRegion = getProductRegion();
                                  
                                  // 检测数据异常
                                  const anomaly = detectDataAnomaly(record, previousRecord);
                                  
                                  return (
                                    <tr 
                                      key={`${date}-${index}`}
                                      className={`${
                                        index === 0 ? 'bg-accent/10' : 
                                        isBaselineData ? 'bg-base-200/50' : 
                                        anomaly.anomalyReasons.length > 0 ? 'bg-red-50 border-l-4 border-red-300' : ''
                                      }`}
                                    >
                                      <td className="font-mono text-sm w-40">
                                        <div className="flex items-center gap-2">
                                          <span className="whitespace-nowrap">{formatTime(record.created_at)}</span>
                                          {index === 0 && (
                                            <span className="badge badge-accent badge-xs">最新</span>
                                          )}
                                          {isBaselineData && (
                                            <span className="badge badge-neutral badge-xs">基准</span>
                                          )}
                                          {isAuto && !isBaselineData && (
                                            <span className="badge badge-primary badge-xs">自动</span>
                                          )}
                                          {anomaly.anomalyReasons.length > 0 && (
                                            <div className="tooltip tooltip-right" data-tip={anomaly.anomalyReasons.join(', ')}>
                                              <span className="text-red-500 text-xs cursor-help">⚠️</span>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                      <td className="font-mono text-sm text-base-content/70 text-center w-44">
                                        <div className="flex items-center justify-center gap-2">
                                          <span className="whitespace-nowrap font-semibold">{formatLocalTime(record.created_at, productRegion)}</span>
                                          <span className="badge badge-ghost badge-sm text-xs whitespace-nowrap bg-gray-100 text-gray-600">
                                            {productRegion}/{getTimezoneAbbr(productRegion)}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="text-center w-24">
                                        {isBaselineData ? (
                                          <span className="text-base-content/50 text-xs whitespace-nowrap">基准数据</span>
                                        ) : (
                                          <span className={`font-semibold whitespace-nowrap ${
                                            anomaly.hasSalesAnomaly ? 'text-red-500' : 
                                            record.sales_increment > 0 ? 'text-success' : 
                                            record.sales_increment < 0 ? 'text-orange-500' : 'text-base-content/50'
                                          }`}>
                                            {record.sales_increment > 0 ? `+${record.sales_increment.toLocaleString('zh-CN')}` :
                                             record.sales_increment < 0 ? record.sales_increment.toLocaleString('zh-CN') :
                                             '0'}
                                          </span>
                                        )}
                                      </td>
                                      <td className="text-center w-24">
                                        {isBaselineData ? (
                                          <span className="text-base-content/50 text-xs whitespace-nowrap">基准数据</span>
                                        ) : (
                                          <span className={`font-semibold whitespace-nowrap ${
                                            anomaly.hasReviewAnomaly ? 'text-red-500' : 
                                            record.review_increment > 0 ? 'text-info' : 
                                            record.review_increment < 0 ? 'text-orange-500' : 'text-base-content/50'
                                          }`}>
                                            {record.review_increment > 0 ? `+${record.review_increment.toLocaleString('zh-CN')}` :
                                             record.review_increment < 0 ? record.review_increment.toLocaleString('zh-CN') :
                                             '0'}
                                          </span>
                                        )}
                                      </td>
                                      <td className="font-semibold text-center w-28">
                                        <span className="whitespace-nowrap">{record.sales_count.toLocaleString('zh-CN')}</span>
                                      </td>
                                      <td className="font-semibold text-center w-28">
                                        <span className="whitespace-nowrap">{record.review_count.toLocaleString('zh-CN')}</span>
                                      </td>
                                      <td className="text-center w-20">
                                        {record.product_rating ? (
                                          <div className="flex items-center justify-center space-x-1">
                                            <span className="text-warning">⭐</span>
                                            <span className="font-semibold">
                                              {record.product_rating.toFixed(1)}
                                            </span>
                                          </div>
                                        ) : (
                                          <span className="text-base-content/50">-</span>
                                        )}
                                      </td>
                                      <td className="text-sm text-center w-20 relative group">
                                        <div className="flex items-center justify-center">
                                        {index === recordsWithIncrements.length - 1 ? (
                                          <span className="text-base-content/50">-</span>
                                        ) : (
                                          <span className="text-base-content/70 font-mono whitespace-nowrap">
                                            {timeInterval}
                                          </span>
                                        )}
                                          
                                          {/* 悬浮删除按钮 */}
                                          <button
                                            onClick={() => handleDeleteRecord(record.id.toString(), formatTime(record.created_at))}
                                            className={`absolute right-1 btn btn-ghost btn-xs text-red-500 hover:bg-red-100 px-1 transition-opacity duration-200 ${
                                              anomaly.anomalyReasons.length > 0 
                                                ? 'opacity-60 group-hover:opacity-100' 
                                                : 'opacity-0 group-hover:opacity-70'
                                            }`}
                                            title={anomaly.anomalyReasons.length > 0 ? "删除异常数据" : "删除记录"}
                                          >
                                            🗑️
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">📅</div>
                  <p className="text-base-content/60">暂无历史记录数据</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* 点击背景关闭 */}
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
};

export default ProductDetailView; 